/**
 * Message Actions
 *
 * Renders assistant message action controls.
 *
 * Primary responsibilities:
 * - Copy, share, regenerate, and save assistant output
 * - Bridge generated output into Space tasks and notes
 *
 * Dependencies:
 * - Clipboard helper
 * - Space item creation action
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Tables } from '@/supabase/types'
import {
  IconArrowUp,
  IconBolt,
  IconBulb,
  IconCheck,
  IconCopy,
  IconDots,
  IconEdit,
  IconLoader2,
  IconPlayerStop,
  IconRepeat,
  IconRefresh,
  IconShare2,
  IconStar,
  IconThumbDown,
  IconThumbUp,
  IconTrash,
  IconCopyPlus,
  IconVolume2,
  IconWorld
} from '@tabler/icons-react'
import katex from 'katex'
import MarkdownIt from 'markdown-it'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { useCopyToClipboard } from '@/lib/hooks/use-copy-to-clipboard'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import type { RegenerateMode, RegenerateRequest } from '@/lib/chat/regenerate-options'
import { reconstructContentWithCodeBlocks } from '@/lib/messages'
import type { CodeBlock } from '@/types'
import { stripSidekickSpaceItemTags } from '@/lib/sidekick/space-item-context'
import { cn } from '@/lib/utils'
import { MESSAGE_ICON_SIZE } from '@/components/messages/message-action-icons'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import {
  AddToSpaceAction,
  type CreatedSpaceNote,
  type CreatedSpaceTask,
  type SpaceNoteDestination
} from '@/components/messages/add-to-space-action'

import { WithTooltip } from '../ui/with-tooltip'
import { MessageShareDialog } from './message-share-dialog'

type PlatformFeedbackRating = 'up' | 'down' | 'neutral'
type InlineFeedbackChoice = 'good' | 'okay' | 'bad'
type InlineFeedbackState = 'waiting' | 'visible' | 'thanks' | 'dismissed'
type InlineFeedbackTrigger = 'click' | 'keyboard'
type StoredInlineFeedbackState = 'submitted' | 'dismissed'

const INLINE_FEEDBACK_PREVIEW_LIMIT = 500
const INLINE_FEEDBACK_SETTLE_DELAY_MS = 4200
const INLINE_FEEDBACK_IDLE_DELAY_MS = 1800
const INLINE_FEEDBACK_RETRY_DELAY_MS = 3000
const INLINE_FEEDBACK_MAX_ATTEMPTS = 3
const INLINE_FEEDBACK_RAGE_SETTLE_DELAY_MS = 1200
const INLINE_FEEDBACK_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000
const INLINE_FEEDBACK_STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000
const INLINE_FEEDBACK_STORAGE_PREFIX = 'losi:inline-feedback:'
const INLINE_FEEDBACK_COOLDOWN_KEY = 'losi:inline-feedback:last-handled-at'
const INLINE_FEEDBACK_BLOCKING_SURFACE_SELECTOR =
  '[role="dialog"], [data-radix-popper-content-wrapper]'
const INLINE_FEEDBACK_RAGE_PATTERN =
  /\b(wtf|fuck|fucking|shit|bullshit|stupid|dumb|trash|hate|angry|mad|furious|frustrated|annoying|broken|not working|doesn['’]?t work|terrible|awful|useless|rage|raging)\b/i

function getInlineFeedbackStorageKey(messageId: string) {
  return `${INLINE_FEEDBACK_STORAGE_PREFIX}${messageId}`
}

function readStoredInlineFeedbackState(messageId: string) {
  if (typeof window === 'undefined') return null

  try {
    const rawValue = window.localStorage.getItem(getInlineFeedbackStorageKey(messageId))
    if (!rawValue) return null

    const parsed = JSON.parse(rawValue) as {
      state?: StoredInlineFeedbackState
      at?: number
    }

    if (
      parsed.at &&
      Date.now() - parsed.at <= INLINE_FEEDBACK_STORAGE_TTL_MS &&
      (parsed.state === 'submitted' || parsed.state === 'dismissed')
    ) {
      return parsed.state
    }

    window.localStorage.removeItem(getInlineFeedbackStorageKey(messageId))
    return null
  } catch {
    return null
  }
}

function rememberInlineFeedbackState(messageId: string, state: StoredInlineFeedbackState) {
  if (typeof window === 'undefined') return

  try {
    const at = Date.now()
    window.localStorage.setItem(
      getInlineFeedbackStorageKey(messageId),
      JSON.stringify({ state, at })
    )
    window.localStorage.setItem(INLINE_FEEDBACK_COOLDOWN_KEY, String(at))
  } catch {
    // Local storage can be unavailable in private or embedded contexts.
  }
}

function rememberInlineFeedbackShown() {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(INLINE_FEEDBACK_COOLDOWN_KEY, String(Date.now()))
  } catch {
    // Local storage can be unavailable in private or embedded contexts.
  }
}

function isInlineFeedbackCoolingDown() {
  if (typeof window === 'undefined') return false

  try {
    const value = Number(window.localStorage.getItem(INLINE_FEEDBACK_COOLDOWN_KEY) ?? 0)
    return Number.isFinite(value) && Date.now() - value < INLINE_FEEDBACK_COOLDOWN_MS
  } catch {
    return false
  }
}

function isEditableElement(element: EventTarget | Element | null) {
  if (!(element instanceof HTMLElement)) return false

  return (
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLInputElement ||
    element.isContentEditable
  )
}

function isActiveEditableBusy() {
  if (typeof document === 'undefined') return false

  const activeElement = document.activeElement
  if (!isEditableElement(activeElement)) return false

  if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
    return activeElement.value.trim().length > 0
  }

  return activeElement.textContent?.trim().length ? true : false
}

function hasBlockingInlineFeedbackSurface() {
  if (typeof document === 'undefined') return false

  return Boolean(document.querySelector(INLINE_FEEDBACK_BLOCKING_SURFACE_SELECTOR))
}

function isRageFeedbackMoment(text?: string | null) {
  return Boolean(text && INLINE_FEEDBACK_RAGE_PATTERN.test(text.slice(-700)))
}

interface MessageActionsProps {
  isGenerating: boolean
  isAssistant: boolean
  isLast: boolean
  isEditing: boolean
  onCopy: () => void
  onEdit?: () => void
  onRegenerate: (request?: RegenerateRequest) => void
  onVoiceToText: () => void
  isVoiceToTextPlaying?: boolean
  content: string // Add content prop
  codeBlocks?: CodeBlock[]
  isLoading?: boolean
  thumbsUp: boolean | null
  onFeedbackUpdate: (
    sessionId: string,
    modelId: string,
    providerId: string,
    messageId: string,
    thumbsUp: boolean | null
  ) => void | Promise<void>
  starred: boolean
  onStarUpdate: (messageId: string, starred: boolean) => void | Promise<void>
  messageId: string
  sessionId: string
  modelId: string
  providerId: string
  onSelect?: (messageId: string) => void
  isSelected?: boolean
  isSelectionMode?: boolean
  onShare?: () => Promise<void>
  onPreview?: () => void
  hasNote?: boolean
  onSpaceNoteCreated?: (note: CreatedSpaceNote, destination: SpaceNoteDestination) => void
  onSpaceTaskCreated?: (task: CreatedSpaceTask, destination: SpaceNoteDestination) => void
  isPublic?: boolean // Hide certain actions in public chat
  sharing?: string | null
  /** When "footer", actions are in a bottom bar. */
  position?: 'header' | 'footer'
  /** Extra controls rendered at the start of the action row (e.g. reactions),
   *  so the inline feedback row below can span the full message width. */
  leadingActions?: ReactNode
  showInlineFeedbackPrompt?: boolean
  inlineFeedbackTriggerText?: string | null
  previousUserMessageContent?: string | null
  onDelete?: () => void
  onDuplicateChat?: () => void
  chatId?: string
}

export function MessageActions({
  isGenerating,
  isAssistant,
  isLast,
  isEditing,
  onCopy,
  onEdit,
  onRegenerate,
  onVoiceToText,
  isVoiceToTextPlaying,
  content,
  codeBlocks,
  isLoading,
  thumbsUp,
  onFeedbackUpdate,
  starred,
  onStarUpdate,
  messageId,
  sessionId,
  modelId,
  providerId,
  onSelect,
  isSelected,
  isSelectionMode,
  onShare,
  hasNote = false,
  onSpaceNoteCreated,
  onSpaceTaskCreated,
  isPublic = false,
  sharing,
  position = 'header',
  leadingActions,
  showInlineFeedbackPrompt = false,
  inlineFeedbackTriggerText = null,
  previousUserMessageContent = null,
  onDelete,
  onDuplicateChat,
  chatId
}: MessageActionsProps) {
  const { t } = useTranslation()
  const [showCheckmark, setShowCheckmark] = useState(false)
  const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout: 2000 })
  const [localStarred, setLocalStarred] = useState<boolean | null>(thumbsUp)
  const [localIsStarred, setLocalIsStarred] = useState(starred)
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [isAddToSpaceOpen, setIsAddToSpaceOpen] = useState(false)
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [pendingFeedback, setPendingFeedback] = useState<boolean | null>(null)
  const [feedbackReason, setFeedbackReason] = useState('')
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const [regeneratePopoverOpen, setRegeneratePopoverOpen] = useState(false)
  const [regenerateInstruction, setRegenerateInstruction] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [inlineFeedbackState, setInlineFeedbackState] = useState<InlineFeedbackState>('waiting')
  const inlineFeedbackAttemptsRef = useRef(0)
  const lastInlineFeedbackActivityRef = useRef(Date.now())
  const isRageMoment = isRageFeedbackMoment(inlineFeedbackTriggerText)
  const isFooter = position === 'footer'
  const footerActionClass =
    'flex size-9 shrink-0 items-center justify-center rounded-lg p-2 transition-colors hover:bg-accent'
  const actionButtonClass = isFooter
    ? footerActionClass
    : cn(
        'size-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-accent',
        'flex'
      )
  const addToSpaceActionClass = isFooter ? footerActionClass : actionButtonClass
  const canConsiderInlineFeedback =
    showInlineFeedbackPrompt &&
    isAssistant &&
    !isPublic &&
    !isGenerating &&
    !isEditing &&
    localStarred === null
  const shouldShowInlineFeedbackPrompt =
    canConsiderInlineFeedback && inlineFeedbackState === 'visible'
  const shouldShowInlineFeedbackThanks =
    showInlineFeedbackPrompt && inlineFeedbackState === 'thanks'

  const renderLatex = (latex: string, displayMode: boolean): string => {
    return katex.renderToString(latex, {
      displayMode: displayMode,
      throwOnError: false
    })
  }

  const submitPlatformFeedback = useCallback(
    async (
      rating: PlatformFeedbackRating,
      reason: string | null,
      metadata: Record<string, unknown> = {}
    ) => {
      try {
        const response = await fetch('/api/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'chat_message',
            context_id: messageId,
            rating,
            reason,
            metadata: {
              modelId: modelId || null,
              providerId: providerId || null,
              messagePreview: stripSidekickSpaceItemTags(content).slice(
                0,
                INLINE_FEEDBACK_PREVIEW_LIMIT
              ),
              ...metadata
            }
          })
        })

        if (!response.ok) {
          throw new Error('Failed to save platform feedback')
        }

        window.dispatchEvent(
          new CustomEvent('losi:platform-feedback-submitted', {
            detail: { source: 'chat_message', rating, contextId: messageId }
          })
        )
      } catch {
        // Best-effort; the message thumbs state is still saved separately.
      }
    },
    [content, messageId, modelId, providerId]
  )

  const commitFeedback = useCallback(
    async (newStarred: boolean | null, reason?: string, metadata?: Record<string, unknown>) => {
      try {
        // Update local state immediately
        setLocalStarred(newStarred)

        if (newStarred !== null) {
          const ratingLabel = newStarred ? 'Good response' : 'Needs work'

          void submitPlatformFeedback(
            newStarred ? 'up' : 'down',
            reason?.trim() || null,
            metadata ?? {
              interaction: 'message_action',
              label: ratingLabel
            }
          )

          window.dispatchEvent(
            new CustomEvent('losi:survey-trigger', {
              detail: { event: 'chat_feedback_rated' }
            })
          )
        }

        // Update parent state which handles backend update
        await onFeedbackUpdate(sessionId, modelId, providerId, messageId, newStarred)

        // Submit feedback to Not Diamond if session data exists
        if (sessionId && modelId && providerId && newStarred !== null) {
          try {
            await fetch('/api/notdiamond/feedback', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId,
                modelId,
                providerId,
                messageId,
                value: newStarred
              })
            })
          } catch (notDiamondError) {
            console.error('Error submitting Not Diamond feedback:', notDiamondError)
            // Don't show error to user - feedback submission is best-effort
          }
        }
      } catch (error) {
        // Revert both local and parent state on error
        setLocalStarred(thumbsUp)
        onFeedbackUpdate(sessionId, modelId, providerId, messageId, thumbsUp)
        console.error('Error updating message feedback:', error)
        toast.error(t("Couldn't save feedback. Please try again."))
      }
    },
    [
      messageId,
      modelId,
      onFeedbackUpdate,
      providerId,
      sessionId,
      submitPlatformFeedback,
      t,
      thumbsUp
    ]
  )

  const toggleStar = useCallback(async () => {
    const newStarred = !localIsStarred
    setLocalIsStarred(newStarred)
    try {
      await onStarUpdate(messageId, newStarred)
    } catch {
      setLocalIsStarred(!newStarred)
      toast.error(t("Couldn't update star. Please try again."))
    }
  }, [localIsStarred, messageId, onStarUpdate, t])

  const dismissInlineFeedback = useCallback(() => {
    rememberInlineFeedbackState(messageId, 'dismissed')
    setInlineFeedbackState('dismissed')
  }, [messageId])

  const submitInlineFeedback = useCallback(
    async (choice: InlineFeedbackChoice, trigger: InlineFeedbackTrigger) => {
      const labels: Record<InlineFeedbackChoice, string> = {
        good: 'Good',
        okay: 'Okay',
        bad: 'Bad'
      }
      const metadata = {
        interaction: 'inline_prompt',
        trigger,
        label: labels[choice]
      }

      rememberInlineFeedbackState(messageId, 'submitted')
      setInlineFeedbackState('thanks')

      if (choice === 'good') {
        await commitFeedback(true, 'Inline quick feedback: Good', metadata)
        return
      }

      if (choice === 'bad') {
        await commitFeedback(false, 'Inline quick feedback: Bad', metadata)
        return
      }

      void submitPlatformFeedback('neutral', 'Inline quick feedback: Okay', metadata)
      window.dispatchEvent(
        new CustomEvent('losi:survey-trigger', {
          detail: { event: 'chat_feedback_rated' }
        })
      )
    },
    [commitFeedback, messageId, submitPlatformFeedback]
  )

  const openFeedbackDialog = (isStarred: boolean) => {
    if (localStarred === isStarred) {
      void commitFeedback(null)
      return
    }

    setPendingFeedback(isStarred)
    setFeedbackReason('')
    setFeedbackDialogOpen(true)
  }

  const submitFeedbackDialog = async (reason?: string) => {
    if (pendingFeedback === null) {
      setFeedbackDialogOpen(false)
      return
    }

    const rating = pendingFeedback
    setPendingFeedback(null)
    setFeedbackSubmitting(true)

    try {
      await commitFeedback(rating, reason)
      setFeedbackReason('')
      setFeedbackDialogOpen(false)
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  const handleCopy = () => {
    const md = new MarkdownIt({
      html: true,
      breaks: true,
      linkify: true
    })

    // Reconstruct code blocks from placeholders before copying
    const fullContent = codeBlocks?.length
      ? reconstructContentWithCodeBlocks(content, codeBlocks)
      : content

    let plainTextContent = stripSidekickSpaceItemTags(fullContent)
    let htmlContent = plainTextContent

    const convertBlockLatexToText = (latex: string): string => {
      return latex
        .replace(/\\text\{([^}]+)\}/g, '$1')
        .replace(/\\cdot/g, '·')
        .replace(/\\times/g, '*')
        .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2')
        .replace(/\s+/g, ' ')
        .trim()
    }

    // Handle block LaTeX
    htmlContent = plainTextContent.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
      htmlContent = htmlContent.replace(match, renderLatex(latex, true))
      return convertBlockLatexToText(latex) // Convert LaTeX to text without delimiters
    })

    // Render the rest of the markdown
    htmlContent = md.render(htmlContent)

    // Copy to clipboard - will automatically transform interactive components
    copyToClipboard(plainTextContent, htmlContent)
    setShowCheckmark(true)

    if (isAssistant) {
      void trackMeaningfulOutcomeCompleted({
        surface: 'chat',
        outcome_type: 'saved_output',
        qualifying_action: 'copied',
        message_id: messageId,
        source: 'assistant_message_action'
      })
    }
  }

  const feedbackDialogTitle =
    pendingFeedback === true ? t('What made this response useful?') : t('What should be improved?')
  const feedbackDialogDescription =
    pendingFeedback === true
      ? t('Your rating is saved either way. A short note helps us repeat what worked.')
      : t('Your rating is saved either way. A short note helps us fix the next answer.')
  const feedbackPlaceholder =
    pendingFeedback === true
      ? t('What was clear, accurate, or especially helpful?')
      : t('What was missing, confusing, or incorrect?')

  const buildRegenerateRequest = (mode: RegenerateMode): RegenerateRequest => {
    const originalPrompt = previousUserMessageContent?.trim() ?? ''
    const instruction = regenerateInstruction.trim()
    const parts: string[] = []

    if (originalPrompt) {
      parts.push(originalPrompt)
    }

    if (mode === 'think') {
      parts.push('Regenerate the answer with a deeper reasoning pass.')
    }

    if (mode === 'web') {
      parts.push(
        'Regenerate the answer with web search. Check current sources before responding and cite useful sources.'
      )
    }

    if (instruction) {
      parts.push(`Change request: ${instruction}`)
    }

    return {
      mode,
      message: parts.join('\n\n').trim() || undefined,
      customInstruction: instruction || undefined,
      forceWebSearch: mode === 'web',
      increaseReasoning: mode === 'think'
    }
  }

  const handleRegenerateChoice = (mode: RegenerateMode) => {
    const request = buildRegenerateRequest(mode)

    setRegeneratePopoverOpen(false)
    setRegenerateInstruction('')
    onRegenerate(request)
  }

  const handleRegenerateWithMini = () => {
    const request = buildRegenerateRequest('retry')
    request.modelOverride = 'losi-mini'

    setRegeneratePopoverOpen(false)
    setRegenerateInstruction('')
    onRegenerate(request)
  }

  useEffect(() => {
    if (showCheckmark) {
      const timer = setTimeout(() => {
        setShowCheckmark(false)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [showCheckmark])

  // keep local star state in sync with parent prop
  useEffect(() => {
    setLocalStarred(thumbsUp)
  }, [thumbsUp])

  useEffect(() => {
    inlineFeedbackAttemptsRef.current = 0
    lastInlineFeedbackActivityRef.current = Date.now()

    if (readStoredInlineFeedbackState(messageId)) {
      setInlineFeedbackState('dismissed')
      return
    }

    setInlineFeedbackState('waiting')
  }, [messageId])

  useEffect(() => {
    if (inlineFeedbackState !== 'thanks') {
      return
    }

    const timer = window.setTimeout(() => {
      setInlineFeedbackState('dismissed')
    }, 2200)

    return () => window.clearTimeout(timer)
  }, [inlineFeedbackState])

  useEffect(() => {
    if (!canConsiderInlineFeedback || inlineFeedbackState !== 'waiting') {
      return
    }

    if (
      readStoredInlineFeedbackState(messageId) ||
      (!isRageMoment && isInlineFeedbackCoolingDown())
    ) {
      setInlineFeedbackState('dismissed')
      return
    }

    let disposed = false
    let timer: number | null = null

    const rememberActivity = () => {
      lastInlineFeedbackActivityRef.current = Date.now()
    }

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
    }

    const scheduleCheck = (delayMs: number) => {
      clearTimer()
      timer = window.setTimeout(checkIdleState, delayMs)
    }

    const checkIdleState = () => {
      if (disposed) return

      const idleForMs = Date.now() - lastInlineFeedbackActivityRef.current
      const isIdle = idleForMs >= INLINE_FEEDBACK_IDLE_DELAY_MS
      const isBlocked =
        document.visibilityState !== 'visible' ||
        isActiveEditableBusy() ||
        hasBlockingInlineFeedbackSurface()

      if (isIdle && !isBlocked) {
        rememberInlineFeedbackShown()
        setInlineFeedbackState('visible')
        return
      }

      inlineFeedbackAttemptsRef.current += 1

      if (inlineFeedbackAttemptsRef.current >= INLINE_FEEDBACK_MAX_ATTEMPTS) {
        setInlineFeedbackState('dismissed')
        return
      }

      scheduleCheck(
        Math.max(INLINE_FEEDBACK_RETRY_DELAY_MS, INLINE_FEEDBACK_IDLE_DELAY_MS - idleForMs)
      )
    }

    window.addEventListener('keydown', rememberActivity, true)
    window.addEventListener('pointerdown', rememberActivity, true)
    window.addEventListener('input', rememberActivity, true)
    window.addEventListener('focusin', rememberActivity, true)
    scheduleCheck(
      isRageMoment ? INLINE_FEEDBACK_RAGE_SETTLE_DELAY_MS : INLINE_FEEDBACK_SETTLE_DELAY_MS
    )

    return () => {
      disposed = true
      clearTimer()
      window.removeEventListener('keydown', rememberActivity, true)
      window.removeEventListener('pointerdown', rememberActivity, true)
      window.removeEventListener('input', rememberActivity, true)
      window.removeEventListener('focusin', rememberActivity, true)
    }
  }, [canConsiderInlineFeedback, inlineFeedbackState, isRageMoment, messageId])

  useEffect(() => {
    if (!shouldShowInlineFeedbackPrompt) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      if (isEditableElement(event.target)) {
        lastInlineFeedbackActivityRef.current = Date.now()
        setInlineFeedbackState('waiting')
        return
      }

      if (hasBlockingInlineFeedbackSurface()) {
        return
      }

      const key = event.key.toLowerCase()
      const isShortcutKey =
        key === '1' || key === '2' || key === '3' || key === 'x' || key === 'escape'

      if (key === '1') {
        event.preventDefault()
        event.stopPropagation()
        void submitInlineFeedback('good', 'keyboard')
        return
      }

      if (key === '2') {
        event.preventDefault()
        event.stopPropagation()
        void submitInlineFeedback('okay', 'keyboard')
        return
      }

      if (key === '3') {
        event.preventDefault()
        event.stopPropagation()
        void submitInlineFeedback('bad', 'keyboard')
        return
      }

      if (key === 'x' || key === 'escape') {
        event.preventDefault()
        event.stopPropagation()
        dismissInlineFeedback()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [dismissInlineFeedback, shouldShowInlineFeedbackPrompt, submitInlineFeedback])

  // Rendered AFTER the action buttons (see below) with basis-full, so it
  // sits on its own row underneath the action row. The parent cluster in
  // message.tsx is flex-1, so that row spans the message width instead of
  // inflating a shrink-to-content ml-auto cluster.
  const inlineFeedbackPrompt = shouldShowInlineFeedbackPrompt ? (
    <div
      className="flex w-full flex-wrap items-center gap-2 rounded-lg border border-border/70 bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-sm"
      data-inline-message-feedback
    >
      <span className="font-medium">{t('How is Losi doing?')}</span>
      <button
        type="button"
        className="flex h-7 items-center gap-1 rounded-md border border-border px-2 text-foreground transition-colors hover:bg-accent"
        onClick={() => void submitInlineFeedback('good', 'click')}
      >
        <span>{t('Good')}</span>
        <kbd className="text-[10px] text-muted-foreground">1</kbd>
      </button>
      <button
        type="button"
        className="flex h-7 items-center gap-1 rounded-md border border-border px-2 text-foreground transition-colors hover:bg-accent"
        onClick={() => void submitInlineFeedback('okay', 'click')}
      >
        <span>{t('Okay')}</span>
        <kbd className="text-[10px] text-muted-foreground">2</kbd>
      </button>
      <button
        type="button"
        className="flex h-7 items-center gap-1 rounded-md border border-border px-2 text-foreground transition-colors hover:bg-accent"
        onClick={() => void submitInlineFeedback('bad', 'click')}
      >
        <span>{t('Bad')}</span>
        <kbd className="text-[10px] text-muted-foreground">3</kbd>
      </button>
      <button
        type="button"
        className="ml-auto flex h-7 items-center gap-1 rounded-md px-2 transition-colors hover:bg-accent"
        onClick={dismissInlineFeedback}
      >
        <span>{t('Dismiss')}</span>
        <kbd className="text-[10px] text-muted-foreground">x</kbd>
      </button>
    </div>
  ) : shouldShowInlineFeedbackThanks ? (
    <div
      className="w-full rounded-lg border border-border/70 bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-sm"
      data-inline-message-feedback-thanks
    >
      {t('Thanks. Feedback saved.')}
    </div>
  ) : null

  return (isLast && isGenerating) || isEditing ? null : (
    <div
      id={!isFooter && isLast ? 'message-actions' : undefined}
      className={cn(
        'text-muted-foreground motion-safe:duration-150 motion-safe:transition-opacity',
        // Footer: column of [action row, inline feedback row below it].
        // Header: single centered row (feedback prompt never shows there).
        isFooter
          ? 'flex w-full min-w-0 flex-1 flex-col gap-2'
          : 'flex flex-wrap items-center justify-center gap-2 space-x-2',
        !isFooter &&
          !isSelectionMode &&
          'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100',
        !isFooter && !isSelectionMode && 'flex'
      )}
    >
      <div
        className={
          isFooter
            ? // w-full + nowrap/scroll on narrow: wrap used to stack every
              // icon in a crushed column when a sibling stole row width.
              'flex min-h-9 w-full flex-nowrap items-center justify-end gap-1 overflow-x-auto sm:gap-2'
            : 'contents'
        }
      >
      {leadingActions}

      {onEdit && (
        <WithTooltip
          asChild
          delayDuration={1000}
          side="bottom"
          display={<div>{t('Edit')}</div>}
          trigger={
            <button
              type="button"
              className={cn(
                isFooter
                  ? footerActionClass
                  : 'flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-accent'
              )}
              onClick={onEdit}
              aria-label={t('Edit')}
            >
              <IconEdit size={MESSAGE_ICON_SIZE} />
            </button>
          }
        />
      )}

      {isAssistant && !isPublic && (onSpaceNoteCreated || onSpaceTaskCreated) && (
        <AddToSpaceAction
          content={content}
          hasNote={hasNote}
          iconClassName={addToSpaceActionClass}
          open={isAddToSpaceOpen}
          position={position}
          onOpenChange={setIsAddToSpaceOpen}
          onNoteCreated={onSpaceNoteCreated}
          onTaskCreated={onSpaceTaskCreated}
        />
      )}
      {isAssistant && !isPublic && (
        <WithTooltip
          asChild
          delayDuration={1000}
          side="bottom"
          display={<div>{t('Good response')}</div>}
          trigger={
            <button
              type="button"
              className={actionButtonClass}
              onClick={() => openFeedbackDialog(true)}
              aria-label={t('Good response')}
            >
              <IconThumbUp
                size={MESSAGE_ICON_SIZE}
                fill={localStarred === true ? 'currentColor' : 'none'}
              />
            </button>
          }
        />
      )}
      {isAssistant && !isPublic && (
        <WithTooltip
          asChild
          delayDuration={1000}
          side="bottom"
          display={<div>{t('Needs work')}</div>}
          trigger={
            <button
              type="button"
              className={actionButtonClass}
              onClick={() => openFeedbackDialog(false)}
              aria-label={t('Needs work')}
            >
              <IconThumbDown
                size={MESSAGE_ICON_SIZE}
                fill={localStarred === false ? 'currentColor' : 'none'}
              />
            </button>
          }
        />
      )}

      {isAssistant && (
        <WithTooltip
          asChild
          delayDuration={1000}
          side="bottom"
          display={<div>{t('Read aloud')}</div>}
          trigger={
            isLoading ? (
              <span className={actionButtonClass} aria-label={t('Loading audio')}>
                <IconLoader2 className="animate-spin" size={MESSAGE_ICON_SIZE} />
              </span>
            ) : isVoiceToTextPlaying ? (
              <button
                type="button"
                className={actionButtonClass}
                onClickCapture={onVoiceToText}
                aria-label={t('Stop reading')}
              >
                <IconPlayerStop size={MESSAGE_ICON_SIZE} />
              </button>
            ) : (
              <button
                type="button"
                className={actionButtonClass}
                onClickCapture={onVoiceToText}
                aria-label={t('Read aloud')}
              >
                <IconVolume2 size={MESSAGE_ICON_SIZE} />
              </button>
            )
          }
        />
      )}

      {isAssistant && isLast && (
        <Popover open={regeneratePopoverOpen} onOpenChange={setRegeneratePopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={actionButtonClass}
              aria-label={t('Regenerate')}
            >
              <IconRepeat size={MESSAGE_ICON_SIZE} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="center"
            side="top"
            sideOffset={8}
            onOpenAutoFocus={(event) => event.preventDefault()}
            className="w-[min(320px,calc(100vw-2rem))] rounded-2xl p-0 shadow-xl"
          >
            <form
              className="border-b p-3"
              onSubmit={(event) => {
                event.preventDefault()
                handleRegenerateChoice('retry')
              }}
            >
              <div className="flex items-center gap-2">
                <input
                  value={regenerateInstruction}
                  onChange={(event) => setRegenerateInstruction(event.target.value)}
                  placeholder={t('Ask to change response')}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
                <button
                  type="submit"
                  className="flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-colors hover:bg-foreground/90"
                  aria-label={t('Regenerate with instruction')}
                >
                  <IconArrowUp size={14} />
                </button>
              </div>
            </form>

            <div className="p-2">
              <button
                type="button"
                className="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors hover:bg-muted"
                onClick={() => handleRegenerateChoice('retry')}
              >
                <IconRefresh className="size-4" />
                <span>{t('Try again')}</span>
              </button>
              <button
                type="button"
                className="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors hover:bg-muted"
                onClick={() => handleRegenerateChoice('think')}
              >
                <IconBulb className="size-4" />
                <span>{t('Think longer')}</span>
              </button>
              <button
                type="button"
                className="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors hover:bg-muted"
                onClick={() => handleRegenerateChoice('web')}
              >
                <IconWorld className="size-4" />
                <span>{t('Search the web')}</span>
              </button>

              {/* Try again with Losi Mini */}
              <button
                type="button"
                className="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors hover:bg-muted"
                onClick={() => handleRegenerateWithMini()}
              >
                <IconBolt className="size-4" />
                <span>{t('Try again with Losi Mini')}</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {isAssistant && !isFooter && (
        <WithTooltip
          asChild
          delayDuration={1000}
          side="bottom"
          display={<div>{t('Copy')}</div>}
          trigger={
            <button
              type="button"
              className={actionButtonClass}
              onClick={handleCopy}
              aria-label={showCheckmark ? t('Copied') : t('Copy')}
            >
              {showCheckmark ? (
                <IconCheck size={MESSAGE_ICON_SIZE} />
              ) : (
                <IconCopy size={MESSAGE_ICON_SIZE} />
              )}
            </button>
          }
        />
      )}
      {isAssistant && isFooter && (
        <button
          type="button"
          onClick={handleCopy}
          className="flex size-9 shrink-0 cursor-pointer touch-manipulation items-center justify-center rounded-lg transition-colors hover:bg-accent active:scale-[0.98]"
          aria-label={t('Copy')}
        >
          {showCheckmark ? (
            <IconCheck size={MESSAGE_ICON_SIZE} />
          ) : (
            <IconCopy size={MESSAGE_ICON_SIZE} />
          )}
        </button>
      )}
      {/* Share moved into the three-dot (More options) menu below. */}
      {/* {1 > 0 && isAssistant && <MessageReplies />} */}
      {/* {1 > 0 && isAssistant && <ChatMessageSelector messageId={messageId} />} */}

      {isAssistant && !isPublic && localIsStarred && (
        <WithTooltip
          asChild
          delayDuration={1000}
          side="bottom"
          display={<div>{t('Starred')}</div>}
          trigger={
            <button
              type="button"
              className={actionButtonClass}
              onClick={() => void toggleStar()}
              aria-label={t('Starred')}
            >
              <IconStar size={MESSAGE_ICON_SIZE} fill="currentColor" className="text-yellow-500" />
            </button>
          }
        />
      )}

      {isAssistant && !isPublic && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={actionButtonClass}
              aria-label={t('More options')}
            >
              <IconDots size={MESSAGE_ICON_SIZE} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="bottom" sideOffset={8}>
            <DropdownMenuItem
              onClick={() => {
                if (onShare) {
                  onShare()
                } else {
                  setIsShareDialogOpen(true)
                }
              }}
            >
              <IconShare2 className="mr-2 size-4" />
              {t('Share message')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void toggleStar()}>
              <IconStar className="mr-2 size-4" fill={localIsStarred ? 'currentColor' : 'none'} />
              {localIsStarred ? t('Unstar message') : t('Star message')}
            </DropdownMenuItem>
            {onDuplicateChat && chatId && <DropdownMenuSeparator />}
            {onDuplicateChat && chatId && (
              <DropdownMenuItem onClick={onDuplicateChat}>
                <IconCopyPlus className="mr-2 size-4" />
                {t('Branch chat here')}
              </DropdownMenuItem>
            )}
            {onDelete && <DropdownMenuSeparator />}
            {onDelete && (
              <DropdownMenuItem
                onClick={() => setDeleteConfirmOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <IconTrash className="mr-2 size-4" />
                {t('Delete message')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* User message three-dot menu */}
      {!isAssistant && !isPublic && (onDelete || onDuplicateChat) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={actionButtonClass}
              aria-label={t('More options')}
            >
              <IconDots size={MESSAGE_ICON_SIZE} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="bottom" sideOffset={8}>
            {onDuplicateChat && chatId ? (
              <DropdownMenuItem onClick={onDuplicateChat}>
                <IconCopyPlus className="mr-2 size-4" />
                {t('Branch chat here')}
              </DropdownMenuItem>
            ) : null}
            {onDuplicateChat && chatId && onDelete ? <DropdownMenuSeparator /> : null}
            {onDelete ? (
              <DropdownMenuItem
                onClick={() => setDeleteConfirmOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <IconTrash className="mr-2 size-4" />
                {t('Delete message')}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {isSelectionMode && (
        <WithTooltip
          asChild
          side="bottom"
          display={<div>{isSelected ? t('Deselect') : t('Select')}</div>}
          trigger={
            <button
              type="button"
              className={`block rounded-full transition-colors ${
                isSelected
                  ? 'bg-black dark:bg-white'
                  : 'rounded-full border-2 border-muted-foreground/80 dark:border-white'
              }`}
              onClick={() => onSelect?.(messageId)}
              aria-label={isSelected ? t('Deselect') : t('Select')}
            >
              <IconCheck
                size={MESSAGE_ICON_SIZE}
                className={`p-1 ${
                  isSelected ? 'text-white dark:text-black' : 'text-muted-foreground'
                }`}
              />
            </button>
          }
        />
      )}
      </div>

      {inlineFeedbackPrompt}

      <MessageShareDialog
        message={{
          id: messageId,
          content,
          sharing: sharing ?? 'private'
        }}
        isAssistant={isAssistant}
        open={isShareDialogOpen}
        setOpen={setIsShareDialogOpen}
      />
      <Dialog
        open={feedbackDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && feedbackDialogOpen && pendingFeedback !== null && !feedbackSubmitting) {
            void submitFeedbackDialog()
            return
          }

          setFeedbackDialogOpen(nextOpen)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{feedbackDialogTitle}</DialogTitle>
            <DialogDescription>{feedbackDialogDescription}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={feedbackReason}
            onChange={(event) => setFeedbackReason(event.target.value)}
            placeholder={feedbackPlaceholder}
            className="min-h-[110px] resize-none"
            maxLength={1000}
          />
          <DialogFooter>
            <Button
              variant="outline"
              disabled={feedbackSubmitting}
              onClick={() => void submitFeedbackDialog()}
            >
              {t('Skip note')}
            </Button>
            <Button
              disabled={feedbackSubmitting}
              onClick={() => void submitFeedbackDialog(feedbackReason.trim())}
            >
              {feedbackSubmitting ? t('Sending...') : t('Send note')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Delete message')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('This will permanently delete this message. This action cannot be undone.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onDelete?.()
                setDeleteConfirmOpen(false)
              }}
            >
              {t('Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
